import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  RotateCcw,
  Wifi,
  WifiOff,
  Zap,
  Camera,
  Clock,
  TrendingUp,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

interface PhotoUploadStatus {
  id: string;
  name: string;
  status: "pending" | "uploading" | "completed" | "failed";
  progress: number;
  size?: number;
  uploadSpeed?: number;
  retryCount?: number;
}

interface EnhancedUploadProgressProps {
  current: number;
  total: number;
  currentPhoto: string;
  status: "preparing" | "uploading" | "registering" | "completed" | "error";
  className?: string;
  failedPhotos?: string[];
  retryCount?: number;
  onRetryFailedPhotos?: () => void;
  onContinue?: () => void;
  // Enhanced tracking
  individualProgress?: Record<
    string,
    {
      status: "pending" | "uploading" | "completed" | "failed";
      progress: number;
    }
  >;
  uploadMode?: "sequential" | "parallel";
  uploadSpeed?: number;
  estimatedTimeRemaining?: number;
  totalSize?: number;
  uploadedSize?: number;
  startTime?: number;
}

export function EnhancedUploadProgress({
  current,
  total,
  currentPhoto,
  status,
  className,
  failedPhotos = [],
  retryCount = 0,
  onRetryFailedPhotos,
  onContinue,
  individualProgress = {},
  uploadMode = "sequential",
  uploadSpeed = 0,
  estimatedTimeRemaining = 0,
  totalSize = 0,
  uploadedSize = 0,
  startTime = Date.now(),
}: EnhancedUploadProgressProps) {
  const [networkQuality, setNetworkQuality] = useState<
    "fast" | "slow" | "offline"
  >("fast");
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [animateSuccess, setAnimateSuccess] = useState(false);
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [lastRealProgress, setLastRealProgress] = useState(0);

  const actualProgress = total > 0 ? (current / total) * 100 : 0;

  // Update elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Smooth progress animation for better UX during parallel uploads
  useEffect(() => {
    let animationFrame: number;
    let progressInterval: NodeJS.Timeout;

    if (status === "uploading" && uploadMode === "parallel") {
      // For parallel uploads, show gradual progress even when photos upload simultaneously
      if (actualProgress === 0 && lastRealProgress === 0) {
        // Start with gradual progress simulation
        progressInterval = setInterval(
          () => {
            setSmoothProgress((prev) => {
              if (prev < 85) {
                // Don't go beyond 85% until we get real completion
                return prev + Math.random() * 8 + 2; // Random increment between 2-10%
              }
              return prev;
            });
          },
          800 + Math.random() * 400,
        ); // Randomized intervals between 800-1200ms
      } else if (actualProgress > lastRealProgress) {
        // Real progress update - smoothly transition to actual progress
        setLastRealProgress(actualProgress);

        const animateToTarget = () => {
          setSmoothProgress((prev) => {
            if (prev < actualProgress) {
              const diff = actualProgress - prev;
              const increment = Math.max(diff * 0.15, 1); // Smooth catch-up
              return Math.min(prev + increment, actualProgress);
            }
            return actualProgress;
          });

          if (smoothProgress < actualProgress) {
            animationFrame = requestAnimationFrame(animateToTarget);
          }
        };

        animateToTarget();
      }
    } else {
      // For non-parallel uploads or other states, use actual progress
      setSmoothProgress(actualProgress);
      setLastRealProgress(actualProgress);
    }

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [actualProgress, status, uploadMode, smoothProgress, lastRealProgress]);

  // Monitor network quality
  useEffect(() => {
    const updateNetworkQuality = () => {
      if (!navigator.onLine) {
        setNetworkQuality("offline");
        return;
      }

      const connection =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection;
      if (connection) {
        const effectiveType = connection.effectiveType;
        if (
          effectiveType === "slow-2g" ||
          effectiveType === "2g" ||
          effectiveType === "3g"
        ) {
          setNetworkQuality("slow");
        } else {
          setNetworkQuality("fast");
        }
      }
    };

    updateNetworkQuality();
    window.addEventListener("online", updateNetworkQuality);
    window.addEventListener("offline", updateNetworkQuality);

    return () => {
      window.removeEventListener("online", updateNetworkQuality);
      window.removeEventListener("offline", updateNetworkQuality);
    };
  }, []);

  // Trigger success animation
  useEffect(() => {
    if (status === "completed" && failedPhotos.length === 0) {
      setAnimateSuccess(true);
      setTimeout(() => setAnimateSuccess(false), 2000);
    }
  }, [status, failedPhotos.length]);

  const formatTimeRemaining = (seconds: number) => {
    if (seconds < 60) return `${Math.ceil(seconds)}s remaining`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${Math.ceil(remainingSeconds)}s remaining`;
  };

  const formatElapsedTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatSize = (mb: number) => {
    if (mb < 1) return `${Math.round(mb * 1024)}KB`;
    return `${mb.toFixed(1)}MB`;
  };

  const getStatusIcon = () => {
    switch (status) {
      case "preparing":
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
      case "uploading":
        return uploadMode === "parallel" ? (
          <div className="relative">
            <Upload className="h-5 w-5 text-blue-500" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
          </div>
        ) : (
          <Upload className="h-5 w-5 animate-pulse text-blue-500" />
        );
      case "registering":
        return <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />;
      case "completed":
        return animateSuccess ? (
          <CheckCircle className="h-5 w-5 text-green-500 animate-bounce" />
        ) : (
          <CheckCircle className="h-5 w-5 text-green-500" />
        );
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Upload className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "preparing":
        return networkQuality === "offline"
          ? "📡 Offline - photos will upload when connection returns"
          : networkQuality === "slow"
            ? "🚀 Optimizing for slow connection..."
            : "⚡ Preparing high-quality upload...";
      case "uploading":
        if (
          uploadMode === "parallel" &&
          Object.keys(individualProgress).length > 0
        ) {
          const completed = Object.values(individualProgress).filter(
            (p) => p.status === "completed",
          ).length;
          const uploading = Object.values(individualProgress).filter(
            (p) => p.status === "uploading",
          ).length;
          let baseText = `🔄 Uploading ${uploading} photos simultaneously (${completed}/${total} done)`;

          if (uploadSpeed > 0 && estimatedTimeRemaining > 0) {
            baseText += `\n💨 ${uploadSpeed.toFixed(1)} MB/s • ${formatTimeRemaining(estimatedTimeRemaining)}`;
          }
          return baseText;
        } else {
          let baseText = `📤 ${currentPhoto}`;
          if (uploadSpeed > 0) {
            baseText += ` • ${uploadSpeed.toFixed(1)} MB/s`;
          }
          return baseText;
        }
      case "registering":
        return "🔐 Securing your uploads and generating access links...";
      case "completed":
        if (failedPhotos.length > 0) {
          return `⚠️ ${failedPhotos.length} photo${failedPhotos.length > 1 ? "s" : ""} need${failedPhotos.length === 1 ? "s" : ""} attention`;
        }
        return "🎉 All photos uploaded successfully!";
      case "error":
        return networkQuality === "offline"
          ? "📡 Upload paused - check your internet connection"
          : "❌ Upload process failed. Please try again.";
      default:
        return "Processing photos...";
    }
  };

  const getQualityInsight = () => {
    if (totalSize === 0) return null;

    const avgPhotoSize = totalSize / total;
    if (avgPhotoSize > 5) {
      return "📸 High resolution photos detected - excellent for dental analysis!";
    } else if (avgPhotoSize > 2) {
      return "✨ Good quality photos - perfect for professional review!";
    } else {
      return "👍 Photos are ready for upload!";
    }
  };

  const photoStats: PhotoUploadStatus[] = Object.entries(
    individualProgress,
  ).map(([id, progress]) => ({
    id,
    name: getPhotoDisplayName(id),
    status: progress.status,
    progress: progress.progress,
  }));

  function getPhotoDisplayName(photoId: string): string {
    const names: Record<string, string> = {
      upper_occlusal: "👆 Upper Teeth",
      lower_occlusal: "👇 Lower Teeth",
      frontal_retracted_apart: "😮 Front Open",
      frontal_in_occlusion: "😊 Front Closed",
      right_buccal: "➡️ Right Side",
      left_buccal: "⬅️ Left Side",
    };
    return names[photoId] || photoId;
  }

  return (
    <Card className={cn("w-full max-w-lg mx-auto overflow-hidden bg-white shadow-lg", className)}>
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h3 className="font-semibold text-gray-900">
                {uploadMode === "parallel"
                  ? "Smart Photo Upload"
                  : "Photo Upload"}
              </h3>
              <p className="text-sm text-gray-600">
                {uploadMode === "parallel"
                  ? "All photos uploading simultaneously"
                  : "Processing your dental photos"}
              </p>
            </div>
          </div>

          {/* Network indicator */}
          <div
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
              networkQuality === "fast"
                ? "bg-green-100 text-green-700"
                : networkQuality === "slow"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-red-100 text-red-700",
            )}
          >
            {networkQuality === "offline" ? (
              <WifiOff className="w-3 h-3" />
            ) : (
              <Wifi className="w-3 h-3" />
            )}
            {networkQuality === "fast"
              ? "Fast"
              : networkQuality === "slow"
                ? "Slow"
                : "Offline"}
          </div>
        </div>

        {/* Status message */}
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-800 whitespace-pre-line">
            {getStatusText()}
          </p>
          {retryCount > 0 && (
            <p className="mt-2 text-xs text-orange-600 flex items-center gap-1">
              <RotateCcw className="w-3 h-3" />
              Retrying failed uploads ({retryCount}/3)
            </p>
          )}
          {getQualityInsight() && (
            <p className="text-xs text-blue-600 mt-2">{getQualityInsight()}</p>
          )}
        </div>

        {/* Enhanced Progress bar with smooth animation */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium">{Math.round(smoothProgress)}%</span>
          </div>
          <div className="relative">
            <Progress
              value={smoothProgress}
              className={cn(
                "h-3 transition-all duration-500",
                status === "completed" &&
                  failedPhotos.length === 0 &&
                  "bg-green-100",
              )}
            />
            {/* Animated progress indicator for parallel uploads */}
            {uploadMode === "parallel" && status === "uploading" && (
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"
                  style={{
                    width: "30%",
                    marginLeft: `${Math.max(0, smoothProgress - 15)}%`,
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>
              {uploadMode === "parallel" && status === "uploading" ? (
                smoothProgress < 90 ? (
                  <span className="flex items-center gap-1">
                    <span className="animate-pulse">Processing...</span>
                    <span className="text-blue-600">
                      ({Math.min(Math.floor(smoothProgress / 17), total)} of{" "}
                      {total} photos)
                    </span>
                  </span>
                ) : (
                  <span>
                    {current} of {total} photos
                  </span>
                )
              ) : (
                <span>
                  {current} of {total} photos
                </span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-gray-400" />
              {formatElapsedTime(elapsedTime)} elapsed
            </span>
          </div>
        </div>

        {/* Individual photo progress for parallel uploads */}
        {uploadMode === "parallel" &&
          Object.keys(individualProgress).length > 0 &&
          status === "uploading" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  Photo Status
                </h4>
                <div className="flex items-center gap-2">
                  {uploadSpeed > 0 && (
                    <div className="text-xs text-blue-600 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {uploadSpeed.toFixed(1)} MB/s
                    </div>
                  )}
                  {smoothProgress < 90 && status === "uploading" && (
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      Optimizing
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {photoStats.map((photo) => (
                  <div
                    key={photo.id}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-lg text-xs transition-all duration-300",
                      photo.status === "completed"
                        ? "bg-green-50 text-green-700 scale-105"
                        : photo.status === "uploading"
                          ? "bg-blue-50 text-blue-700 animate-pulse"
                          : photo.status === "failed"
                            ? "bg-red-50 text-red-700"
                            : "bg-gray-50 text-gray-600",
                    )}
                  >
                    {photo.status === "pending" && (
                      <Camera className="w-3 h-3 opacity-60" />
                    )}
                    {photo.status === "uploading" && (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    )}
                    {photo.status === "completed" && (
                      <CheckCircle className="w-3 h-3 animate-bounce" />
                    )}
                    {photo.status === "failed" && (
                      <AlertCircle className="w-3 h-3" />
                    )}
                    <span className="flex-1 truncate font-medium">
                      {photo.name}
                    </span>
                    {photo.status === "completed" && (
                      <span className="text-xs opacity-75">✨</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Performance stats toggle */}
        {(uploadSpeed > 0 || totalSize > 0) && (
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Shield className="w-3 h-3" />
              <span>Secure encrypted upload</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetailedStats(!showDetailedStats)}
              className="text-xs h-auto p-1"
            >
              <Zap className="w-3 h-3 mr-1" />
              Stats
            </Button>
          </div>
        )}

        {/* Detailed stats panel */}
        {showDetailedStats && (uploadSpeed > 0 || totalSize > 0) && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-3">
              {uploadSpeed > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Speed:</span>
                  <span className="font-medium text-blue-600">
                    {uploadSpeed.toFixed(1)} MB/s
                  </span>
                </div>
              )}
              {estimatedTimeRemaining > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">ETA:</span>
                  <span className="font-medium text-orange-600">
                    {formatTimeRemaining(estimatedTimeRemaining)}
                  </span>
                </div>
              )}
              {totalSize > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Size:</span>
                  <span className="font-medium">{formatSize(totalSize)}</span>
                </div>
              )}
              {uploadedSize > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Uploaded:</span>
                  <span className="font-medium text-green-600">
                    {formatSize(uploadedSize)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Retry prompt for failed uploads */}
        {status === "completed" && failedPhotos.length > 0 && (
          <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-orange-900 mb-1">
                  Upload Issues Detected
                </h4>
                <p className="text-xs text-orange-700 mb-2">
                  {failedPhotos.length} photo
                  {failedPhotos.length > 1 ? "s" : ""} couldn't be uploaded:{" "}
                  {failedPhotos.map(getPhotoDisplayName).join(", ")}
                </p>
                <p className="text-xs text-orange-600">
                  This is usually due to network issues. You can retry or
                  continue - your photos are saved locally.
                </p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              {onRetryFailedPhotos && (
                <Button
                  onClick={onRetryFailedPhotos}
                  size="sm"
                  variant="outline"
                  className="text-orange-700 border-orange-300 hover:bg-orange-100"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Retry Upload
                </Button>
              )}
              {onContinue && (
                <Button
                  onClick={onContinue}
                  size="sm"
                  variant="outline"
                  className="text-gray-700 border-gray-300 hover:bg-gray-100"
                >
                  Continue Anyway
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Success message */}
        {status === "completed" && failedPhotos.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <CheckCircle
              className={cn(
                "w-8 h-8 text-green-600 mx-auto mb-2",
                animateSuccess && "animate-bounce",
              )}
            />
            <p className="text-sm font-medium text-green-900">
              Perfect! All photos uploaded successfully
            </p>
            <p className="text-xs text-green-700 mt-1">
              Your high-quality dental photos are now securely stored and ready
              for professional analysis.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
